import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        language: state.context.session.language,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    container?: InjectableCss;
    dataTable?: DataTableProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    webOrder?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    webOrderCells?: DataTableCellProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    orderDateHeader: {
        css: css`
            position: sticky;
            left: 0;
            width: 150px;
            background-clip: padding-box;
        `,
    },
    orderDateCells: {
        css: css`
            position: sticky;
            left: 0;
            width: 150px;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}
`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    erpOrderNumber: ["erpOrderNumber"],
    orderDate: ["orderDate", "erpOrderNumber", "webOrderNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPO: ["customerPO"],
    webOrderNumber: ["webOrderNumber"],
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
        if (this.props.canViewOrders) {
            recentOrdersParameter.sort = sort;
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        const rows = ordersDataView.value.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language: this.props.language,
                      })
                    : "",
                orderNumber: order.erpOrderNumber,
                shipTo: `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                isVmiOrder: !!order.vmiLocationId,
                webOrderNumber: order.webOrderNumber,
            };
        });

        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList
                    css={css`
                        padding-bottom: 50px;
                    `}
                    extendedStyles={styles.cardList}
                    data-test-selector="widget_RecentOrders"
                >
                    <CardListHeading heading={translate("Recent Orders")} viewAllUrl={orderHistoryUrl} />
                    {ordersDataView.value.length === 0 && (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Typography
                                className="no-orders-found-text"
                                {...styles.noOrdersFoundText}
                                data-test-selector="recentOrders_NoOrdersFound"
                            >
                                {translate("No orders found")}
                            </Typography>
                        </GridItem>
                    )}
                </CardList>
                {rows.length > 0 && (
                    <StyledWrapper {...styles.container}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader
                                    {...styles.orderDateHeader}
                                    sorted={this.sorted("orderDate")}
                                    onSortClick={() => this.headerClick("orderDate")}
                                    data-test-selector="tableHeader_OrderDate"
                                >
                                    {translate("Date", "orderDate")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.orderNumberHeader}
                                    title={translate("Order Number")}
                                    sorted={this.sorted("erpOrderNumber")}
                                    onSortClick={() => this.headerClick("erpOrderNumber")}
                                    data-test-selector="tableHeader_webOrderNumber"
                                >
                                    {translate("Order #", "webOrderNumber")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.shipToHeader}
                                    sorted={this.sorted("stCompanyName")}
                                    onSortClick={() => this.headerClick("stCompanyName")}
                                >
                                    {translate("Ship To / Pick Up", "stCompanyName")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.statusHeader}
                                    sorted={this.sorted("status")}
                                    onSortClick={() => this.headerClick("status")}
                                    data-test-selector="tableHeader_status"
                                >
                                    {translate("Status", "status")}
                                </DataTableHeader>
                                {settingsCollection.orderSettings.showWebOrderNumber && (
                                    <DataTableHeader
                                        {...styles.webOrder}
                                        sorted={this.sorted("webOrderNumber")}
                                        onSortClick={() => this.headerClick("webOrderNumber")}
                                    >
                                        {translate("Web Order #", "webOrderNumber")}
                                    </DataTableHeader>
                                )}
                                <DataTableHeader
                                    {...styles.customerPOHeader}
                                    title={translate("Purchase Order Number")}
                                    sorted={this.sorted("customerPO")}
                                    onSortClick={() => this.headerClick("customerPO")}
                                >
                                    {translate("PO #", "customerPO")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.orderTotalHeader}
                                    sorted={this.sorted("orderTotal")}
                                    onSortClick={() => this.headerClick("orderTotal")}
                                >
                                    {translate("Total", "orderTotal")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                                {rows.map(
                                    ({
                                        id,
                                        linkOrderNumber,
                                        date,
                                        orderNumber,
                                        shipTo,
                                        status,
                                        po,
                                        total,
                                        isVmiOrder,
                                        webOrderNumber,
                                    }) => (
                                        <DataTableRow key={id}>
                                            <DataTableCell
                                                {...styles.orderDateCells}
                                                data-test-selector="orderHistoryTable_tableCell_date"
                                            >
                                                <OrderDetailPageTypeLink
                                                    title={date}
                                                    orderNumber={linkOrderNumber}
                                                    isVmiOrder={isVmiOrder}
                                                    isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                                />
                                            </DataTableCell>

                                            <DataTableCell
                                                {...styles.orderNumberCells}
                                                data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                            >
                                                <OrderDetailPageTypeLink
                                                    title={orderNumber}
                                                    orderNumber={orderNumber}
                                                    isVmiOrder={isVmiOrder}
                                                    isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                                />
                                            </DataTableCell>
                                            <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="orderHistoryTable_tableCell_status"
                                            >
                                                {status}
                                            </DataTableCell>
                                            {settingsCollection.orderSettings.showWebOrderNumber && (
                                                <DataTableCell {...styles.webOrderCells}>
                                                    {webOrderNumber}
                                                </DataTableCell>
                                            )}
                                            <DataTableCell {...styles.customerPOCells}>{po}</DataTableCell>
                                            <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RecentOrders)),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
